import { z } from 'zod';
import {
  BasicQuery,
  EntityReference,
  TimeFrame,
  Metric,
  GraphsData,
  ThresholdFilter,
} from './webapp-common.dto';

export const EntryFunction = z.object({
  id: z.number(),
  name: z.string(),
});
export type EntryFunction = z.infer<typeof EntryFunction>;

export const EndpointMetrics = z.object({
  runs: Metric,
  normalizedRuns: Metric,
  durationAvg: Metric,
  durationP90: Metric,
  errorRate: Metric,
});
export type EndpointMetrics = z.infer<typeof EndpointMetrics>;

export const Endpoint = z.object({
  id: z.number(),
  path: z.string(),
  methods: z.array(z.string()),
  functionId: z.number(),
  service: EntityReference,
  firstSeenAt: z.coerce.date(),
  lastSeenAt: z.coerce.date().nullable(),
  entryFunction: z.nullable(EntryFunction).optional(),
  metrics: EndpointMetrics,
});
export type Endpoint = z.infer<typeof Endpoint>;

export const GetEndpointsResponse = z.object({
  total: z.number(),
  countFiltered: z.number(), // if no filter is applied, this will be the same as total
  endpoints: z.array(Endpoint),
});
export type GetEndpointsResponse = z.infer<typeof GetEndpointsResponse>;

export const EndpointSortableKeys = Endpoint.pick({
  id: true,
  path: true,
  lastSeenAt: true,
  service: true,
})
  .extend({
    'normalizedRuns.value': z.string(),
    'runs.value': z.string(),
    'runs.trend': z.string(),
    'durationAvg.value': z.string(),
    'durationAvg.trend': z.string(),
    'durationP90.value': z.string(),
    'durationP90.trend': z.string(),
    'errorRate.value': z.string(),
    'errorRate.trend': z.string(),
    runs: z.string(),
    durationAvg: z.string(),
    durationP90: z.string(),
    errorRate: z.string(),
  })
  .keyof();
export type EndpointSortableKeys = z.infer<typeof EndpointSortableKeys>;

export const StatusCodeBreakdownItem = z.object({
  name: z.string(),
  metrics: z.object({
    count: Metric,
    normalizedCount: Metric,
    distribution: Metric,
  }),
});
export type StatusCodeBreakdownItem = z.infer<typeof StatusCodeBreakdownItem>;

export const StatusCodeBreakdown = z.array(StatusCodeBreakdownItem);
export type StatusCodeBreakdown = z.infer<typeof StatusCodeBreakdown>;

const FlowDeployment = z.object({
  functionIds: z.array(z.number()),
  timestamp: z.coerce.date(),
});
export type FlowDeployment = z.infer<typeof FlowDeployment>;
export const FlowDeployments = z.array(FlowDeployment);
export type FlowDeployments = z.infer<typeof FlowDeployments>;

export const GetEndpointsQuery = BasicQuery.extend({
  orderBy: z
    .object({
      key: EndpointSortableKeys,
      sortOrder: z.union([z.literal('asc'), z.literal('desc')]),
    })
    .optional(),
  methods: z.array(z.string()).optional(),
  serviceIds: z.array(z.coerce.number()).optional(),
  functionIds: z.array(z.coerce.number()).optional(),
  endpointIds: z.array(z.coerce.number()).optional(),
  normalizedRuns: ThresholdFilter.optional(),
  durationAvg: ThresholdFilter.optional(),
  errorRate: ThresholdFilter.optional(),
  runsThreshold: ThresholdFilter.optional(),
});
export type GetEndpointsQuery = z.infer<typeof GetEndpointsQuery>;

export const GetEndpointQuery = z.object({
  environmentName: z.string().optional(),
  timeFrame: TimeFrame.optional(),
  tags: z.record(z.string()).optional(),
});
export type GetEndpointQuery = z.infer<typeof GetEndpointQuery>;

export const GetEndpointResponse = z.object({
  endpoint: Endpoint,
  graphs: GraphsData,
  deployments: FlowDeployments,
  statusCodes: StatusCodeBreakdown,
});
export type GetEndpointResponse = z.infer<typeof GetEndpointResponse>;

export const GetEndpointsCountQuery = GetEndpointQuery.extend({
  flowType: z.string().optional(),
  flowTypeGroup: z.string().optional(),
});
export type GetEndpointsCountQuery = z.infer<typeof GetEndpointsCountQuery>;
